import React from 'react';
import { Box, Container, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const ContactSection = styled(Box)`
  padding: 6rem 0;
  background: linear-gradient(135deg, #030b17 0%, #0a1929 100%);
  position: relative;
  overflow: hidden;
  min-height: 60vh;
  display: flex;
  align-items: center;
`;

const BackgroundPattern = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 30%, rgba(79, 172, 254, 0.1) 0%, transparent 30%),
    radial-gradient(circle at 80% 70%, rgba(0, 242, 254, 0.1) 0%, transparent 30%),
    linear-gradient(rgba(79, 172, 254, 0.03) 2px, transparent 2px),
    linear-gradient(90deg, rgba(79, 172, 254, 0.03) 2px, transparent 2px);
  background-size: 100% 100%, 100% 100%, 50px 50px, 50px 50px;
  background-position: 0 0, 0 0, -1px -1px, -1px -1px;
  z-index: 0;
  animation: patternMove 30s linear infinite;
  
  @keyframes patternMove {
    0% { background-position: 0 0, 0 0, -1px -1px, -1px -1px; }
    100% { background-position: 100px 100px, -100px -100px, -1px -1px, -1px -1px; }
  }
`;

const ContentWrapper = styled(Container)`
  position: relative;
  z-index: 2;
`;

const Title = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 700;
  color: #f5f5f5;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  max-width: 700px;
  margin: 0 auto 4rem;
  line-height: 1.8;
`;

const ContactCard = styled(motion.div)`
  background: rgba(10, 25, 41, 0.7);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(79, 172, 254, 0.2);
  max-width: 800px;
  margin: 0 auto;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(79, 172, 254, 0.4);
    box-shadow: 0 25px 50px rgba(79, 172, 254, 0.2);
  }
`;

const InfoItem = styled(Box).withConfig({
  shouldForwardProp: (prop) => prop !== 'component'
})<{ component?: React.ElementType } & React.AnchorHTMLAttributes<HTMLAnchorElement>>`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: rgba(79, 172, 254, 0.05);
  border-radius: 16px;
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    background: rgba(79, 172, 254, 0.1);
    transform: translateX(5px);
  }
  
  svg {
    color: #4facfe;
    font-size: 2rem;
    margin-right: 1.5rem;
    filter: drop-shadow(0 0 10px rgba(79, 172, 254, 0.3));
  }
  
  h4 {
    color: #f5f5f5;
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0 0 0.4rem;
    letter-spacing: 0.5px;
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
    font-size: 1.1rem;
    letter-spacing: 0.3px;
    line-height: 1.5;
  }
`;

const MapContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 250px;
  border-radius: 16px;
  overflow: hidden;
  margin-top: 1.5rem;
  border: 1px solid rgba(79, 172, 254, 0.2);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const Contact = () => {
  return (
    <ContactSection id="contact">
      <BackgroundPattern />
      
      <ContentWrapper maxWidth="lg">
        <Title
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          🚀 Let's Build Something Great Together!
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Ready to transform your ideas into reality? Our expert team combines cutting-edge technology with industry expertise to create solutions that drive your business forward.
        </Subtitle>
        
        <ContactCard
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Grid container spacing={3} sx={{ padding: '20px' }}>
            <Grid item xs={12} sm={6}>
              <InfoItem 
                component="a"
                href="https://www.google.com/maps/dir//Lucknow,+Uttar+Pradesh,+India"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  height: '200px', 
                  backgroundColor: '#020B1A',
                  borderRadius: '10px',
                  padding: '30px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '20px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7)',
                  border: '1px solid rgba(100, 255, 218, 0.05)',
                  position: 'relative',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at top left, rgba(100, 255, 218, 0.08), transparent 70%)',
                    borderRadius: '10px',
                    pointerEvents: 'none'
                  },
                  '&:hover': {
                    border: '1px solid rgba(100, 255, 218, 0.15)',
                    transform: 'translateY(-5px)',
                    transition: 'all 0.3s ease-in-out',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.8)'
                  }
                }}>
                <LocationOnIcon sx={{ color: '#64FFDA', fontSize: '3rem', filter: 'drop-shadow(0 0 15px rgba(100, 255, 218, 0.4))' }} />
                <Box sx={{ width: '100%' }}>
                  <h4 style={{ fontSize: '1.75rem', color: '#CCD6F6', marginBottom: '12px', fontWeight: '600', textShadow: '0 0 25px rgba(100, 255, 218, 0.15)' }}>Location</h4>
                  <p style={{ color: '#8892B0', fontSize: '1.1rem', lineHeight: '1.6' }}>Lucknow, Uttar Pradesh, India</p>
                </Box>
              </InfoItem>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <InfoItem 
                component="a"
                href="mailto:yurixventures@gmail.com"
                sx={{ 
                  height: '200px', 
                  backgroundColor: '#020B1A',
                  borderRadius: '10px',
                  padding: '30px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '20px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7)',
                  border: '1px solid rgba(100, 255, 218, 0.05)',
                  position: 'relative',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at top left, rgba(100, 255, 218, 0.08), transparent 70%)',
                    borderRadius: '10px',
                    pointerEvents: 'none'
                  },
                  '&:hover': {
                    border: '1px solid rgba(100, 255, 218, 0.15)',
                    transform: 'translateY(-5px)',
                    transition: 'all 0.3s ease-in-out',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.8)'
                  }
                }}>
                <EmailIcon sx={{ color: '#64FFDA', fontSize: '3rem', filter: 'drop-shadow(0 0 15px rgba(100, 255, 218, 0.4))' }} />
                <Box sx={{ width: '100%' }}>
                  <h4 style={{ fontSize: '1.75rem', color: '#CCD6F6', marginBottom: '12px', fontWeight: '600', textShadow: '0 0 25px rgba(100, 255, 218, 0.15)' }}>Email</h4>
                  <p style={{ color: '#8892B0', fontSize: '1.1rem', lineHeight: '1.6' }}>yurixventures@gmail.com</p>
                </Box>
              </InfoItem>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <InfoItem 
                component="a"
                href="https://wa.me/918756992565"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  height: '200px', 
                  backgroundColor: '#020B1A',
                  borderRadius: '10px',
                  padding: '30px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '20px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7)',
                  border: '1px solid rgba(100, 255, 218, 0.05)',
                  position: 'relative',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at top left, rgba(100, 255, 218, 0.08), transparent 70%)',
                    borderRadius: '10px',
                    pointerEvents: 'none'
                  },
                  '&:hover': {
                    border: '1px solid rgba(100, 255, 218, 0.15)',
                    transform: 'translateY(-5px)',
                    transition: 'all 0.3s ease-in-out',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.8)'
                  }
                }}>
                <PhoneIcon sx={{ color: '#64FFDA', fontSize: '3rem', filter: 'drop-shadow(0 0 15px rgba(100, 255, 218, 0.4))' }} />
                <Box sx={{ width: '100%' }}>
                  <h4 style={{ fontSize: '1.75rem', color: '#CCD6F6', marginBottom: '12px', fontWeight: '600', textShadow: '0 0 25px rgba(100, 255, 218, 0.15)' }}>Phone</h4>
                  <p style={{ color: '#8892B0', fontSize: '1.1rem', lineHeight: '1.6' }}>+91 8756992565</p>
                </Box>
              </InfoItem>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <InfoItem sx={{ 
                height: '200px', 
                backgroundColor: '#020B1A',
                borderRadius: '10px',
                padding: '30px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '20px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7)',
                border: '1px solid rgba(100, 255, 218, 0.05)',
                position: 'relative',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'radial-gradient(circle at top left, rgba(100, 255, 218, 0.08), transparent 70%)',
                  borderRadius: '10px',
                  pointerEvents: 'none'
                },
                '&:hover': {
                  border: '1px solid rgba(100, 255, 218, 0.15)',
                  transform: 'translateY(-5px)',
                  transition: 'all 0.3s ease-in-out',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.8)'
                }
              }}>
                <AccessTimeIcon sx={{ color: '#64FFDA', fontSize: '3rem', filter: 'drop-shadow(0 0 15px rgba(100, 255, 218, 0.4))' }} />
                <Box sx={{ width: '100%' }}>
                  <h4 style={{ fontSize: '1.75rem', color: '#CCD6F6', marginBottom: '12px', fontWeight: '600', textShadow: '0 0 25px rgba(100, 255, 218, 0.15)' }}>Business Hours</h4>
                  <p style={{ color: '#8892B0', fontSize: '1.1rem', lineHeight: '1.6', whiteSpace: 'nowrap' }}>Mon - Fri: 9:00 AM - 6:00 PM IST</p>
                </Box>
              </InfoItem>
            </Grid>
          </Grid>
          
          <MapContainer
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d227819.98391279557!2d80.74670212393495!3d26.848623337909686!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399bfd991f32b16b%3A0x93ccba8909978be7!2sLucknow%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1701536622037!5m2!1sen!2sin"
              allowFullScreen
              loading="lazy"
              title="Yurix office location in Lucknow, Uttar Pradesh"
            />
          </MapContainer>
        </ContactCard>
      </ContentWrapper>
    </ContactSection>
  );
};

export default Contact;